import React, { useEffect, useState } from "react";
import axios from "axios";
import { auth } from "../../../../../../firebase/firebase";
import { getApiUrl } from "../../../../../../config/api";

/**
 * CategoriesSelection
 *
 * Fetches all categories from the backend and lets the user pick
 * a category + sub-category before proceeding to PDF upload.
 * Also provides a modal to create a brand-new category (+ optional sub-category).
 *
 * Props:
 *   language        – "en" | "zh"
 *   onBack          – () => void
 *   onNext          – ({ categoryId, categoryName, subCategoryId, subCategoryName }) => void
 */

/* ── tiny reusable colour map (matches UploadMenu card palette) ───────── */
const colorMap = {
  orange: {
    bg: "bg-orange-500/10",
    border: "border-orange-500/50",
    hoverBorder: "hover:border-orange-500/50",
    iconText: "text-orange-500",
    ring: "hover:ring-orange-500/20",
  },
  gray: {
    bg: "bg-gray-100",
    border: "border-gray-300",
    hoverBorder: "hover:border-gray-400",
    iconText: "text-gray-500",
    ring: "hover:ring-gray-300/30",
  },
};

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
/*  Add-Category Modal                                                    */
/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
function AddCategoryModal({ language, open, onClose, onCreated }) {
  const [categoryName, setCategoryName] = useState("");
  const [subCategoryName, setSubCategoryName] = useState("");
  const [subSubCategoryName, setSubSubCategoryName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Reset state every time the modal opens
  useEffect(() => {
    if (open) {
      setCategoryName("");
      setSubCategoryName("");
      setSubSubCategoryName("");
      setError("");
      setSaving(false);
    }
  }, [open]);

  const handleCreate = async () => {
    const trimmedCat = categoryName.trim();
    if (!trimmedCat) return;
    setSaving(true);
    setError("");

    try {
      const token = await auth.currentUser?.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };

      const trimmedSub = subCategoryName.trim();
      const trimmedSubSub = subSubCategoryName.trim();

      // Create the category (+ optional sub / sub-sub in one call — the
      // backend ensures the parent chain exists before adding each level).
      await axios.post(
        `${getApiUrl()}/api/v2/categories/`,
        {
          category_name: trimmedCat,
          sub_category_name: trimmedSub,
          sub_sub_category_name: trimmedSubSub,
        },
        { headers },
      );

      onCreated(); // signal parent to refresh the list
      onClose();
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        (language === "zh"
          ? "建立失敗，請再試一次。"
          : "Creation failed. Please try again.");
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    /* Backdrop */
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      {/* Modal card */}
      <div className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl animate-slideUp">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="p-6">
          {/* Header icon */}
          <div className="w-12 h-12 bg-orange-500/10 rounded-2xl flex items-center justify-center mb-4 text-orange-500">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 4v16m8-8H4"
              />
            </svg>
          </div>

          <h3 className="text-lg font-bold text-gray-900 mb-1">
            {language === "zh" ? "新增類別" : "Add New Category"}
          </h3>
          <p className="text-sm text-gray-500 mb-6">
            {language === "zh"
              ? "輸入類別名稱，子類別為選填。"
              : "Enter a category name. Sub-category is optional."}
          </p>

          {/* ── Form ── */}
          <div className="space-y-4">
            {/* Category name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                {language === "zh" ? "類別名稱" : "Category Name"}
                <span className="text-red-400 ml-0.5">*</span>
              </label>
              <input
                type="text"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder={
                  language === "zh"
                    ? "例：Thermal Interface Material"
                    : "e.g. Thermal Interface Material"
                }
                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all"
              />
            </div>

            {/* Sub-category name (optional) */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                {language === "zh" ? "子類別名稱" : "Sub-category Name"}
                <span className="text-gray-300 text-xs ml-1">
                  ({language === "zh" ? "選填" : "optional"})
                </span>
              </label>
              <input
                type="text"
                value={subCategoryName}
                onChange={(e) => setSubCategoryName(e.target.value)}
                placeholder={
                  language === "zh"
                    ? "例：High Performance Thermal Pad"
                    : "e.g. High Performance Thermal Pad"
                }
                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all"
              />
            </div>

            {/* Sub-sub-category name (optional, needs a sub-category) */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                {language === "zh" ? "細分類別名稱" : "Sub-sub-category Name"}
                <span className="text-gray-300 text-xs ml-1">
                  ({language === "zh" ? "選填" : "optional"})
                </span>
              </label>
              <input
                type="text"
                value={subSubCategoryName}
                onChange={(e) => setSubSubCategoryName(e.target.value)}
                disabled={!subCategoryName.trim()}
                placeholder={
                  language === "zh"
                    ? "例：Non Silicone Thermal Pad"
                    : "e.g. Non Silicone Thermal Pad"
                }
                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
              />
              {!subCategoryName.trim() && (
                <p className="text-xs text-gray-400 mt-1">
                  {language === "zh"
                    ? "需先填寫子類別"
                    : "Enter a sub-category first"}
                </p>
              )}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mt-4 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
            >
              {language === "zh" ? "取消" : "Cancel"}
            </button>
            <button
              onClick={handleCreate}
              disabled={saving || !categoryName.trim()}
              className={`px-6 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 ${
                saving || !categoryName.trim()
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-orange-500 text-white hover:bg-orange-600 shadow-md shadow-orange-500/20"
              }`}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {language === "zh" ? "建立中…" : "Creating…"}
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  {language === "zh" ? "建立" : "Create"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
/*  Main Component                                                        */
/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
function CategoriesSelection({ language, onBack, onNext }) {
  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [fetchError, setFetchError] = useState("");

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [selectedSubSub, setSelectedSubSub] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);

  // ── Fetch ─────────────────────────────────────────────────────────────
  const fetchCategories = async () => {
    setLoadingCats(true);
    setFetchError("");
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await axios.get(`${getApiUrl()}/api/v2/categories/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(res.data.data || []);
    } catch {
      setFetchError(
        language === "zh"
          ? "無法載入類別，請稍後再試。"
          : "Failed to load categories. Please try again.",
      );
    } finally {
      setLoadingCats(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectCategory = (cat) => {
    setSelectedCategory(cat);
    setSelectedSub(null);
    setSelectedSubSub(null);
  };

  const handleSelectSub = (sub) => {
    setSelectedSub(sub);
    setSelectedSubSub(null);
  };

  // Validation: a category is required; a sub-category is required only when
  // the category has any; a sub-sub-category is required only when the chosen
  // sub-category has any.
  const catNeedsSub = selectedCategory?.sub_categories?.length > 0;
  const subNeedsSubSub = selectedSub?.sub_sub_categories?.length > 0;
  const canProceed =
    selectedCategory !== null &&
    (!catNeedsSub || selectedSub !== null) &&
    (!subNeedsSubSub || selectedSubSub !== null);

  const handleNext = () => {
    if (!canProceed) return;
    onNext({
      categoryId: selectedCategory.category_id,
      categoryName: selectedCategory.category_name,
      subCategoryId: selectedSub?.category_id ?? null,
      subCategoryName: selectedSub?.sub_category ?? null,
      subSubCategoryId: selectedSubSub?.category_id ?? null,
      subSubCategoryName: selectedSubSub?.sub_sub_category ?? null,
    });
  };

  // ── Loading ───────────────────────────────────────────────────────────
  if (loadingCats) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-500 dark:text-gray-100 text-sm">
          {language === "zh" ? "載入類別中…" : "Loading categories…"}
        </p>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────
  if (fetchError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <p className="text-red-500 text-sm">{fetchError}</p>
        <button
          onClick={fetchCategories}
          className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          {language === "zh" ? "重試" : "Retry"}
        </button>
      </div>
    );
  }

  return (
    <div className="animate-slideUp flex justify-center">
      <div className="w-full max-w-4xl">
        {/* ── Header row ── */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {language === "zh" ? "選擇產品類別" : "Select Product Category"}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-300 mt-0.5">
                {language === "zh"
                  ? "請先選擇類別與子類別，再上傳 PDF"
                  : "Choose a category and sub-category before uploading the PDF"}
              </p>
            </div>
          </div>

          {/* Add-category button */}
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-orange-600 dark:text-gray-100 bg-orange-500/10 dark:bg-orange-500 hover:bg-orange-500/20 border-2 border-orange-500/20 hover:border-orange-500/40 rounded-xl transition-all duration-200"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            {language === "zh" ? "新增類別" : "New Category"}
          </button>
        </div>

        {/* ── Step 1 – Category cards (UploadMenu-style) ── */}
        <div className="mb-8">
          <p className="text-xs font-semibold text-gray-400 dark:text-gray-300 uppercase tracking-wider mb-3">
            {language === "zh"
              ? "步驟 1 — 選擇類別"
              : "Step 1 — Select Category"}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((cat) => {
              const isSelected =
                selectedCategory?.category_id === cat.category_id;
              return (
                <button
                  key={cat.category_id}
                  onClick={() => handleSelectCategory(cat)}
                  className={`group bg-white border-2 rounded-2xl p-6 text-left transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 hover:ring-4 focus:outline-none focus:ring-4 ${
                    isSelected
                      ? `${colorMap.orange.border} ring-4 ring-orange-500/20 shadow-lg shadow-orange-500/10`
                      : `border-gray-200 ${colorMap.orange.hoverBorder} ${colorMap.orange.ring}`
                  }`}
                >
                  {/* Icon */}
                  <div
                    className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 ${
                      isSelected
                        ? "bg-orange-500 text-white"
                        : "bg-orange-500/10 text-orange-500"
                    }`}
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                      />
                    </svg>
                  </div>

                  {/* Name */}
                  <h3
                    className={`text-sm font-bold mb-1 ${isSelected ? "text-orange-600" : "text-gray-900"}`}
                  >
                    {cat.category_name}
                  </h3>

                  {/* Sub-count badge */}
                  {cat.sub_categories?.length > 0 && (
                    <p className="text-xs text-gray-400">
                      {cat.sub_categories.length}{" "}
                      {language === "zh" ? "個子類別" : "sub-categories"}
                    </p>
                  )}

                  {/* Check indicator */}
                  {isSelected && (
                    <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-orange-500">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {language === "zh" ? "已選擇" : "Selected"}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Step 2 – Sub-category cards ── */}
        {selectedCategory && selectedCategory.sub_categories?.length > 0 && (
          <div className="mb-8">
            <p className="text-xs font-semibold text-gray-400 dark:text-gray-200 uppercase tracking-wider mb-3">
              {language === "zh"
                ? "步驟 2 — 選擇子類別"
                : "Step 2 — Select Sub-category"}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedCategory.sub_categories.map((sub) => {
                const isSelected = selectedSub?.category_id === sub.category_id;
                return (
                  <button
                    key={sub.category_id}
                    onClick={() => handleSelectSub(sub)}
                    className={`group bg-white border-2 rounded-2xl p-5 text-left transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 hover:ring-4 focus:outline-none focus:ring-4 ${
                      isSelected
                        ? `${colorMap.orange.border} ring-4 ring-orange-500/20 shadow-md shadow-orange-500/10`
                        : `border-gray-200 ${colorMap.orange.hoverBorder} ${colorMap.orange.ring}`
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                          isSelected
                            ? "bg-orange-500 text-white"
                            : "bg-orange-500/10 text-orange-500"
                        }`}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z"
                          />
                        </svg>
                      </div>
                      <span
                        className={`text-sm font-semibold ${isSelected ? "text-orange-600" : "text-gray-800"}`}
                      >
                        {sub.sub_category}
                      </span>
                      {isSelected && (
                        <svg
                          className="w-5 h-5 text-orange-500 ml-auto flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Step 3 – Sub-sub-category cards ── */}
        {selectedSub && selectedSub.sub_sub_categories?.length > 0 && (
          <div className="mb-8">
            <p className="text-xs font-semibold text-gray-400 dark:text-gray-200 uppercase tracking-wider mb-3">
              {language === "zh"
                ? "步驟 3 — 選擇細分類別"
                : "Step 3 — Select Sub-sub-category"}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedSub.sub_sub_categories.map((subSub) => {
                const isSelected =
                  selectedSubSub?.category_id === subSub.category_id;
                return (
                  <button
                    key={subSub.category_id}
                    onClick={() => setSelectedSubSub(subSub)}
                    className={`group bg-white border-2 rounded-2xl p-5 text-left transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 hover:ring-4 focus:outline-none focus:ring-4 ${
                      isSelected
                        ? `${colorMap.orange.border} ring-4 ring-orange-500/20 shadow-md shadow-orange-500/10`
                        : `border-gray-200 ${colorMap.orange.hoverBorder} ${colorMap.orange.ring}`
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                          isSelected
                            ? "bg-orange-500 text-white"
                            : "bg-orange-500/10 text-orange-500"
                        }`}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 6h16M4 10h16M4 14h10M4 18h10"
                          />
                        </svg>
                      </div>
                      <span
                        className={`text-sm font-semibold ${isSelected ? "text-orange-600" : "text-gray-800"}`}
                      >
                        {subSub.sub_sub_category}
                      </span>
                      {isSelected && (
                        <svg
                          className="w-5 h-5 text-orange-500 ml-auto flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Summary pill ── */}
        {canProceed && (
          <div className="mb-6 px-5 py-3 bg-orange-50 border border-orange-200 rounded-2xl flex items-center gap-2.5 text-sm text-orange-700">
            <svg
              className="w-5 h-5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span>
              <span className="font-semibold">
                {selectedCategory.category_name}
              </span>
              {selectedSub && (
                <>
                  {" › "}
                  <span className="font-semibold">
                    {selectedSub.sub_category}
                  </span>
                </>
              )}
              {selectedSubSub && (
                <>
                  {" › "}
                  <span className="font-semibold">
                    {selectedSubSub.sub_sub_category}
                  </span>
                </>
              )}
            </span>
          </div>
        )}

        {/* ── Action buttons ── */}
        <div className="flex items-center justify-between pt-2">
          <button
            onClick={onBack}
            className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
          >
            {language === "zh" ? "返回" : "Back"}
          </button>
          <button
            onClick={handleNext}
            disabled={!canProceed}
            className={`px-6 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 ${
              canProceed
                ? "bg-orange-500 text-white hover:bg-orange-600 shadow-md shadow-orange-500/20 hover:shadow-lg hover:shadow-orange-500/30"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
          >
            {language === "zh" ? "下一步：上傳 PDF" : "Next: Upload PDF"}
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* ── Add-Category Modal ── */}
      <AddCategoryModal
        language={language}
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCreated={() => {
          setSelectedCategory(null);
          setSelectedSub(null);
          setSelectedSubSub(null);
          fetchCategories();
        }}
      />
    </div>
  );
}

export default CategoriesSelection;
